import './group-component.css';

import { clone, isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/core/Button';
import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttributeTypeName, Item } from '@/types/DataModel';

import { useVisibleMemberAttributes } from '../Collection/useMemberAttributes';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { validateMultiDimensionalEntry } from '../MultiDimensional/validating';
import { EditorComponent } from '../types';
import { GroupEntry } from './GroupEntry';

interface GroupComponentProps extends EditorComponent {
    // Group specific props go here
}

// Returns true when `value` carries any user-visible payload, i.e. a primitive
// other than '' / null / undefined or an array/object with at least one such
// nested value. Used to decide whether adding/duplicating a group entry should
// dirty the form: empty-template entries (LAX-9559) must not, but entries
// pre-populated by a custom service (e.g. assets picked from the asset
// folders modal) must (LAX-9787).
const hasMeaningfulContent = (value: unknown): boolean => {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (Array.isArray(value)) {
        return value.some(hasMeaningfulContent);
    }
    if (typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).some(hasMeaningfulContent);
    }
    return true;
};

const GroupComponent = ({
    attribute,
    direction,
    beforeAttributeControl,
    fieldPath = attribute.name,
    hidden = false,
    readonly = false,
}: GroupComponentProps) => {
    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    const DEFAULT_WIDGET_SERVICE = 'DefaultWidgetService';
    const customServiceName = Array.isArray(attribute.params?.renderer)
        ? attribute.params?.renderer.find((params: any) => params?.key === 'service')?.value
        : undefined;
    const service = $rootScope?.getService(customServiceName || DEFAULT_WIDGET_SERVICE);

    const { addLocalValidationError, removeLocalValidationError, removeLocalValidationErrorByAttributeName } =
        useValidationsStore(
            useShallow((state) => ({
                addLocalValidationError: state.addLocalValidationError,
                removeLocalValidationError: state.removeLocalValidationError,
                removeLocalValidationErrorByAttributeName: state.removeLocalValidationErrorByAttributeName,
            })),
        );

    const focusRef = useFocusOnAttribute<HTMLButtonElement>(attribute);

    const { t } = useTranslation();
    const { getValues, setValue, control, formState: { isDirty } } = useFormContext();

    const { dataModel } = useDataModel();
    const { fields, remove, replace } = useFieldArray({
        control,
        name: fieldPath,
    });

    const watchedValues = useWatch({
        name: fieldPath,
    });
    const item = getValues() as Item;

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const [expandedGroupEntries, setExpandedGroupEntries] = useState<boolean[]>(
        fields?.length && fields.length > 0
            ? Array.from({ length: fields.length }, () => true)
            : [],
    );

    const memberAttributes = useVisibleMemberAttributes(attribute, dataModel);

    const onExpandGroup = (index: number) => {
        setExpandedGroupEntries((prevState: any) => ({
            ...prevState,
            [index]: !prevState[index],
        }));
    };

    const expandGroups = (from: number, to: number) => {
        setExpandedGroupEntries(prevState => {
            const newState = Array.isArray(prevState) ? [...prevState] : [];
            for (let i = from; i < to; i++) {
                newState[i] = true;
            }
            return newState;
        });
    };

    const onAddGroup = async (index: number) => {
        try {
            const result = await service.executeAction('addEntry', attribute, fields, index, item);

            if (result) {
                const currentValues = getValues(fieldPath) || [];
                const itemsToAdd = Array.isArray(result) ? result : [result];
                const newValues = [...currentValues, ...itemsToAdd];
                const shouldDirty = isDirty || itemsToAdd.some(hasMeaningfulContent);
                setValue(fieldPath, newValues, { shouldDirty });

                const addedCount = result.length ?? 1;
                const fromIndex = index + 1;
                const toIndex = fromIndex + addedCount;

                expandGroups(fromIndex, toIndex);
            }
        } catch (error) {
            logger.error('Failed to add group entry', error);
        }
    };

    const onRemoveGroup = async (index: number) => {
        try {
            const shouldRemove = await service
                .executeAction('deleteEntry', attribute, fields, index, item);

            if (shouldRemove) {
                remove(index);
                const updatedShowGroupData = { ...expandedGroupEntries };
                delete updatedShowGroupData[index];
                setExpandedGroupEntries(updatedShowGroupData);
            }
        } catch (error) {
            logger.error('Failed to delete group entry', error);
        }
    };

    const onDuplicateGroup = async (index: number) => {
        try {

            // Get actual values from the form
            const currentValues = getValues(`${fieldPath}.${index}`);
            if (!currentValues) return;
            const clonedField = clone(currentValues);
            const rows = getValues(fieldPath) || [];
            const shouldDirty = isDirty || hasMeaningfulContent(clonedField);
            setValue(fieldPath, [...rows, clonedField], { shouldDirty });

            onExpandGroup(fields.length);

        } catch (error) {
            logger.error('Failed to copy group entry', error);
        }
    };

    const onSetEntry = async (index: number) => {
        try {
            const result = await service
                .executeAction('setEntry', attribute, fields, index, item);

            if (result) {
                replace(result);
                if (!expandedGroupEntries[index]) {
                    onExpandGroup(index);
                }
            }
        } catch (error) {
            logger.error('Failed to set group entry', error);
        }
    };
    const actionEnabled = (action: string) => {
        return service?.getActions(attribute, fields).includes(action);
    };

    useEffect(() => {
        if (
            attribute.typeName !== DataModelAttributeTypeName.MultiDimensional
        ) {
            return;
        }

        const valuesToValidate = watchedValues || (fields as any[]);

        // Clear all local validation errors for this attribute
        // This ensures errors are cleared when validation passes or when values are updated
        removeLocalValidationErrorByAttributeName(fieldPath);

        if (!isEmpty(valuesToValidate)) {
            const groups = valuesToValidate;

            groups.forEach((group: any, index: any) => {
                const groupErrors = validateMultiDimensionalEntry<any>(
                    group,
                    groups,
                    attribute,
                );

                removeLocalValidationError(fieldPath);
                removeLocalValidationError(`${fieldPath}.${index}`);
                if (!isEmpty(groupErrors)) {
                    groupErrors.forEach((error: any) => {
                        addLocalValidationError(fieldPath, {
                            name: 'LOCAL_VALIDATION',
                            attributeName: fieldPath,
                            message: t(error),
                            level: 'Failure',
                            path: [attribute.name],
                        });
                        addLocalValidationError(`${fieldPath}.${index}`, {
                            name: 'LOCAL_VALIDATION',
                            attributeName: fieldPath,
                            message: t(error),
                            level: 'Failure',
                            path: [attribute.name],
                        });
                    });
                }
            });
        }
    }, [attribute, fieldPath, addLocalValidationError, removeLocalValidationError, removeLocalValidationErrorByAttributeName, t, watchedValues, fields]);

    if (!memberAttributes) {
        logger.error(`GroupComponent: Value attribute ${attribute.name} has no members`);
        return null;
    }

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            fieldPath={fieldPath}
            direction={
                direction ??
                (fields?.length > 0 ? 'vertical' : 'horizontal')
            }
            gridMode={false}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
        >
            {((!fields || fields?.length < 1 ) && actionEnabled('addEntry'))?(
                <div className={readonly ?
                    'tw-border tw-border-gray-300 tw-rounded-[4px] tw-bg-[#dedede] tw-px-2 tw-py-2 tw-h-[34px]'
                    : 'tw-flex tw-flex-row'}>
                    {(readonly && isEmpty(fields)) ?
                        t('GROUP.EMPTY')
                        : <Button
                            type="button"
                            onClick={() => onAddGroup(-1)}
                            disabled={readonly}
                            ref={focusRef}
                        >
                            {t('ADD_ENTRY')}
                        </Button>
                    }
                </div>
            ): <></>}
            {(fields && !isEmpty(fields)) &&
                fields.map((_: Record<'id', string>, index: number) => {
                    return (
                        <GroupEntry
                            key={_.id}
                            attribute={attribute}
                            fields = {fields}
                            fieldPath={fieldPath}
                            index={index}
                            isExpanded={expandedGroupEntries[index]}
                            memberAttributes={memberAttributes}
                            onAdd={onAddGroup}
                            onDuplicate={onDuplicateGroup}
                            onRemove={onRemoveGroup}
                            onSetEntry= {onSetEntry}
                            onTitleClick={onExpandGroup}
                            readonly={readonly}
                        />
                    );
                })}
        </Layout>
    );
};

export { GroupComponent };
